import { parseBoolean } from '../helper/parse-helper';
import { sceneHelper } from '../helper/scene-helper';
import type { AnimationManager } from '../animation-manager';
import { audioManager as sharedAudioManager, type AudioManager } from '../audio-manager';
import * as THREE from 'three';
import type { Object3D } from 'three';

let _animationManager: AnimationManager | null = null;
let _audioManager: AudioManager | null = null;

export function initStopPlaybackImplementation(
  animationManager: AnimationManager,
  audioManager: AudioManager
) {
  _animationManager = animationManager;
  _audioManager = audioManager;
}

function stopMediaElement(element: HTMLMediaElement) {
  if (!element) return;
  try {
    element.pause();
    element.currentTime = 0;
    if (element instanceof HTMLAudioElement) {
      element.removeAttribute('src');
      element.load();
    }
  } catch {
    /* some elements throw when currentTime is reset */
  }
}

function collectThreeAudioFromObject(rootObject: Object3D, audioNodes: Set<Object3D>) {
  if (!rootObject || typeof rootObject.traverse !== 'function') return;
  rootObject.traverse((obj) => {
    const audioObj = obj as Object3D & { isAudio?: boolean; isPositionalAudio?: boolean };
    if (audioObj.isAudio === true || audioObj.isPositionalAudio === true) {
      audioNodes.add(obj);
    }
  });
}

function stopThreeAudioNode(
  audioNode: Object3D & { isPlaying?: boolean; stop?: () => void; pause?: () => void }
) {
  if (!audioNode) return;
  try {
    if (typeof audioNode.stop === 'function') {
      audioNode.stop();
    } else if (typeof audioNode.pause === 'function') {
      audioNode.pause();
    }
    if (typeof audioNode.isPlaying === 'boolean') {
      audioNode.isPlaying = false;
    }
  } catch {
    /* three.js Audio throws if stopped before start() */
  }
}

function collectDomMediaElements(elements: Set<HTMLMediaElement>) {
  if (typeof document === 'undefined') return;
  for (const el of document.querySelectorAll<HTMLMediaElement>('audio, video')) {
    elements.add(el);
  }
}

export function collectVideoElementsFromObject(rootObject: Object3D, videoElements: Set<HTMLVideoElement>) {
  if (!rootObject) return;
  rootObject.traverse((obj) => {
    const material = (obj as THREE.Mesh).material;
    const materials = Array.isArray(material) ? material : material ? [material] : [];
    for (const mat of materials) {
      const meshMat = mat as THREE.MeshStandardMaterial;
      const mapImage = meshMat.map?.image;
      if (mapImage instanceof HTMLVideoElement) {
        videoElements.add(mapImage);
      }
      const emissiveMapImage = meshMat.emissiveMap?.image;
      if (emissiveMapImage instanceof HTMLVideoElement) {
        videoElements.add(emissiveMapImage);
      }
    }
  });
}

export function stopVideos({
  allTargets,
  targetId,
}: {
  allTargets: boolean;
  targetId?: string;
}) {
  const videoElements = new Set<HTMLVideoElement>();
  if (allTargets) {
    collectVideoElementsFromObject(sceneHelper?.getSceneRoot()!, videoElements);
  } else if (targetId) {
    collectVideoElementsFromObject(sceneHelper?.getObjectByUUID(targetId)!, videoElements);
  }

  for (const videoElement of videoElements) {
    stopMediaElement(videoElement);
  }
}

function runStopPlaybackChannels(
  data: {
    stopAnimation?: boolean | string;
    stopVideo?: boolean | string;
    stopAudio?: boolean | string;
    target?: { id?: string };
  },
  allTargets: boolean
) {
  const stopAnimation = parseBoolean(data?.stopAnimation);
  const stopVideo = parseBoolean(data?.stopVideo);
  const stopAudio = parseBoolean(data?.stopAudio);
  const targetId = data?.target?.id;

  if (!allTargets && !targetId) return;

  if (stopAnimation) {
    runStopAnimation({ allTargets, targetId });
  }

  if (stopVideo) {
    stopVideos({ allTargets, targetId });
  }

  if (stopAudio) {
    runStopAudio({ allTargets, targetId });
  }
}

export function runStopPlaybackImplementation(data: {
  stopAnimation?: boolean | string;
  stopVideo?: boolean | string;
  stopAudio?: boolean | string;
  target?: { id?: string };
}) {
  runStopPlaybackChannels(data, false);
}

export function runStopAllPlaybackImplementation(data: {
  stopAnimation?: boolean | string;
  stopVideo?: boolean | string;
  stopAudio?: boolean | string;
}) {
  runStopPlaybackChannels(data, true);
}

export function runStopAnimation({
  allTargets,
  targetId,
  clip,
  transitionDuration = 0,
}: {
  allTargets: boolean;
  targetId?: string;
  clip?: string;
  transitionDuration?: number;
}) {
  if (allTargets) _animationManager!.stopAllAnimations();
  else if (targetId) _animationManager!.stopAnimation(targetId, clip, transitionDuration);
}

function resolveAudioManager(): AudioManager | null {
  return _audioManager ?? sharedAudioManager;
}

export function runStopAudio({ allTargets = true, targetId }: { allTargets?: boolean; targetId?: string } = {}) {
  const audioManager = resolveAudioManager();
  if (!audioManager) return;
  audioManager.stop('scene');

  const threeAudio = new Set<Object3D>();
  if (allTargets) {
    const root = sceneHelper?.getSceneRoot();
    if (root) collectThreeAudioFromObject(root, threeAudio);
  } else if (targetId) {
    const obj = sceneHelper?.getObjectByUUID(targetId);
    if (obj) collectThreeAudioFromObject(obj, threeAudio);
  }
  for (const audioNode of threeAudio) {
    stopThreeAudioNode(audioNode as Object3D & { isPlaying?: boolean; stop?: () => void });
  }

  if (allTargets) {
    const mediaElements = new Set<HTMLMediaElement>();
    collectDomMediaElements(mediaElements);
    for (const el of mediaElements) stopMediaElement(el);
  }
}
